import {
  Box,
  BulkActionsContext,
  FilterContext,
  Flex,
  ModalContext,
  PaginationContext,
  SearchContext,
  Text,
} from 'dash-ui';
import {
  PortfolioFragment,
  useGetPortfolioFolderItemsQuery,
  useObservePropertiesChangesSubscription,
} from 'gql/generated.graphql';
import { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import PortfolioPropertiesListView from './components/PortfolioPropertiesListView/PortfolioPropertiesListView';
import { SubtitleWrapper } from './PortfoliosDetailView.styles';

const PortfolioDetailView = ({
  portfolio,
}: {
  portfolio: PortfolioFragment;
}) => {
  const theme = useTheme();
  const { t } = useTranslation('pages');
  const { isOpenCurrent } = useContext(ModalContext);
  const { selectedItems } = useContext(BulkActionsContext);
  const { state, setTotal, getRefetchProps, setPerPage } =
    useContext(PaginationContext);
  const { searchSlug } = useContext(SearchContext);
  const searchText = searchSlug.length > 2 ? searchSlug : null;
  const { state: filterState } = useContext(FilterContext);
  const { sort = {} } = filterState;
  const { skip, limit } = getRefetchProps(state);
  const { data, loading, refetch } = useGetPortfolioFolderItemsQuery({
    variables: {
      search: searchText,
      portfolioFolderId: portfolio._id,
      pagination: {
        skip,
        limit,
      },
      sort:
        (sort.sortOptions || []).length > 0
          ? sort.sortOptions
          : [{ fieldName: 'property.name', sortOrder: 1 }],
    },
    onCompleted: (result) => {
      setTotal(result?.getPortfolioFolderItems.totalCount || 0);
    },
  });
  useObservePropertiesChangesSubscription();

  const { portfolioItems } = useMemo(
    () => ({
      portfolioItems: data?.getPortfolioFolderItems.collection || [],
    }),
    [data, isOpenCurrent]
  );

  useEffect(() => {
    setPerPage(12);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isOpenCurrent['MovePropertyToFolderModal']) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems, isOpenCurrent, portfolioItems]);

  return (
    <Box>
      {!portfolioItems.length && !loading ? (
        <Flex
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          h={500}
        >
          {searchSlug ? (
            <Text
              color={theme.colors.n80}
            >{t`common:placeholders.noSearchResults`}</Text>
          ) : (
            <>
              <Text semibold lg>
                {t('portfoliosPage.emptyPageLabel')}
              </Text>
              <SubtitleWrapper>
                <Text color={theme.colors.n80}>
                  {t('portfoliosPage.emptyPageSubLabel')}
                </Text>
              </SubtitleWrapper>
            </>
          )}
        </Flex>
      ) : (
        <Box>
          <PortfolioPropertiesListView
            portfolioItems={portfolioItems}
            loading={loading}
          />
        </Box>
      )}
    </Box>
  );
};

export default PortfolioDetailView;
